import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePSNavigate } from '../../hooks/usePSNavigate'
import { CacheFactory } from '../../models/CacheFactory'
import { Field } from '../../models/Field'
import { PSRecord } from '../../models/PSRecord'
import { RecordDataMap } from '../../models/RecordDataMap'
import { RecordFactory } from '../../models/RecordFactory'
import { Type } from '../../models/Type'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { VERIFY_BUSINESS_FORM } from '../../utils/constants'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { yearsInBusinessOptions } from '../settings/Options'
import { WorkflowPage } from '../workflows/WorkflowPage'

export function SetupDashboardVerifyBusinessPage() {
    const { logAndDisplayError } = useErrorHandler()
    const { data, isLoading } = useQuery({
        queryKey: ['onboarding', 'gl_business'],
        queryFn: async () => {
            const syncResult = await RecordFactory.getInstance().fetchOnboardingState('gl_business')
            if (!syncResult.ok) {
                logAndDisplayError(syncResult.error)
                return null
            }

            const verifyBusinessForm =
                await RecordFactory.getInstance().loadType(VERIFY_BUSINESS_FORM)
            if (!verifyBusinessForm) {
                logAndDisplayError(new RecordNotFoundError('Could not find verify business form'))
                return null
            }

            const fields = [
                'custom_how_old_is_your_business',
                'custom_urls_for_websitefbigetc',
                'custom_what_services_do_you_offer',
                'custom_if_referred_let_us_know_who',
            ]

            // Must add fields to cache factory to be able to send them in the post data
            fields.forEach((field) => {
                CacheFactory.getInstance().addObject(
                    Field.cacheKey(field, verifyBusinessForm!.owner!.id!, verifyBusinessForm!.id),
                    'string'
                )
            })

            return { verifyBusinessForm: verifyBusinessForm as Type }
        },
    })

    const { advanceOnboardingState, growYourBusinessDetails, setGrowYourBusinessDetails } =
        useOnboardingContext()

    const user = PSApplication.me()
    const navigate = usePSNavigate()
    const [searchParams] = useSearchParams()
    const banner = searchParams.get('banner')
    const queryClient = useQueryClient()
    const [isSaving, setIsSaving] = useState(false)

    const nextEnabled = useMemo(() => {
        return (
            growYourBusinessDetails?.yearsInBusiness &&
            growYourBusinessDetails?.servicesOffered &&
            growYourBusinessDetails?.marketingInfo
        )
    }, [growYourBusinessDetails])

    const onNextHandler = async () => {
        if (!data) {
            return
        }

        const record = PSRecord.initWithType(data.verifyBusinessForm)
        record.fieldValues = new RecordDataMap({
            custom_how_old_is_your_business: growYourBusinessDetails?.yearsInBusiness,
            custom_urls_for_websitefbigetc: growYourBusinessDetails?.marketingInfo,
            custom_what_services_do_you_offer: growYourBusinessDetails?.servicesOffered,
            custom_if_referred_let_us_know_who: growYourBusinessDetails?.referralName,
        })
        record.person = user

        setIsSaving(true)
        const results = await RecordFactory.getInstance().sendRecord(record, false, false)
        setIsSaving(false)

        if (!results.ok) {
            logAndDisplayError(results.error)
            return
        }

        if (banner) {
            await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
            return navigate('/')
        }

        await advanceOnboardingState()
    }

    if (isLoading || !data) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <Spinner />
            </div>
        )
    }

    return (
        <WorkflowPage
            onClose={() => navigate('/')}
            onBack={() => navigate('/golive/welcome')}
            backButtonTitle="Payments"
            footerContent={
                <div className="flex justify-end gap-x-4">
                    <Button
                        intent="secondary"
                        type="submit"
                        disabled={isSaving}
                        onClick={async () => {
                            await advanceOnboardingState()
                        }}
                    >
                        Skip
                    </Button>
                    <Button
                        intent="primary"
                        type="submit"
                        isLoading={isSaving}
                        disabled={!nextEnabled}
                        onClick={onNextHandler}
                    >
                        Next
                    </Button>
                </div>
            }
        >
            <Heading size="xl" className="mt-[5px]">
                Tell us about your business
            </Heading>
            <Paragraph size="md" className="mt-4 text-center text-gray-600">
                Let us know a little bit more about your business and the services you provide
            </Paragraph>
            <WorkflowPage.Card className="mt-12">
                <WorkflowPage.IconSection
                    title="How long has your business been in operation? (required)"
                    icon="calendar.large"
                >
                    <SearchableSelect
                        size="medium"
                        items={yearsInBusinessOptions.map((option) => ({
                            id: option.value,
                            title: option.label,
                        }))}
                        onSelectItem={(item) => {
                            if (item === null) return
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                yearsInBusiness: item.id,
                            }))
                        }}
                        selectedItemID={growYourBusinessDetails?.yearsInBusiness}
                        shouldPreserveLastSelection
                    />
                </WorkflowPage.IconSection>
                <WorkflowPage.IconSection
                    title="What services do you offer? (required)"
                    icon="tags.large"
                >
                    <TextArea
                        value={growYourBusinessDetails?.servicesOffered}
                        onChange={(e) =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                servicesOffered: e.target.value,
                            }))
                        }
                        onClear={() =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                servicesOffered: '',
                            }))
                        }
                        placeholder="Enter services offered"
                        size="medium"
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="How do you market your services (required)"
                    icon="megaphone.large"
                >
                    <TextArea
                        value={growYourBusinessDetails?.marketingInfo}
                        onChange={(e) =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                marketingInfo: e.target.value,
                            }))
                        }
                        onClear={() =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                marketingInfo: '',
                            }))
                        }
                        placeholder="Enter how you market your services"
                        size="medium"
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Were you referred by someone?"
                    icon="users.large"
                    description="Let us know who referred you and we’ll be sure to thank them."
                >
                    <TextField
                        value={growYourBusinessDetails?.referralName}
                        onChange={(e) =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                referralName: e.target.value,
                            }))
                        }
                        onClear={() =>
                            setGrowYourBusinessDetails((s) => ({
                                ...s,
                                referralName: '',
                            }))
                        }
                        placeholder="Add referrer name"
                        size="medium"
                    />
                </WorkflowPage.IconSection>
            </WorkflowPage.Card>
        </WorkflowPage>
    )
}
